import type { BatteryConcertApplicationDto } from "~/api/dto/battery-concert-application.dto"

import {
  concertApplicationOutcome as sharedConcertApplicationOutcome,
  type ConcertOutcome,
  type ConcertOutcomeTone,
} from "../../../ProposalSetup/financing/concert/concert-application-copy"

export {
  CONCERT_EYEBROW,
  CONCERT_STAGES,
  CONCERT_SUPPORT_GUIDANCE,
  concertActivityLabel,
  concertDateLabel,
  concertStage,
  concertTermsRows,
  formatConcertTerm,
  GENERATE_LOAN_DOCUMENTS_BUSY_LABEL,
  GENERATE_LOAN_DOCUMENTS_LABEL,
  isResolvedStipulation,
  LOAN_DOCUMENT_STATUS_COPY,
  LOAN_DOCUMENTS_HEADING,
  LOAN_DOCUMENTS_PREPARING_DETAIL,
  OUTCOME_TONE_CLASS,
  REPLACE_DOCUMENT_LABEL,
  RETRY_APPLICATION_LABEL,
  SEND_LOAN_DOCUMENTS_BUSY_LABEL,
  SEND_LOAN_DOCUMENTS_LABEL,
  STIPULATION_PILL_CLASS,
  STIPULATION_STATUS_LABEL,
  SUBMIT_DOCUMENTS_FOR_REVIEW_LABEL,
  TERM_KEYS,
  UPLOAD_DOCUMENT_LABEL,
  type ConcertOutcome,
  type ConcertOutcomeTone,
  type ConcertTermsRow,
} from "../../../ProposalSetup/financing/concert/concert-application-copy"

/** Concert Battery Finance Partner Vision finance type. */
export const CONCERT_BATTERY_FINANCE_TYPE = `concert_battery`

/**
 * Supporting-document upload and submit-for-review are held back while the
 * Concert underwriting flow is still being validated. The gateway operations
 * and their hooks stay in place; only the controls are withheld, so restoring
 * them is this one switch.
 */
export const SUPPORTING_DOCUMENT_ACTIONS_ENABLED = false

export const NO_CONCERT_APPLICATION_MESSAGE = `No Existing Loan Application yet. Use Reserve now on the proposal page to apply.`
export const CONCERT_SETUP_FAILED_STATUS_MESSAGE = `Concert customer setup failed. Contact support to correct the customer details, then use Reserve now again.`
export const CONCERT_SETUP_UNCONFIRMED_STATUS_MESSAGE = `Concert customer setup is not confirmed. Contact support before trying Reserve now again.`

/** The Battery reading of a Concert application's outcome. */
export const concertApplicationOutcome = (
  application: BatteryConcertApplicationDto,
): ConcertOutcome => sharedConcertApplicationOutcome(application, `Battery`)

export const SUPPLEMENTAL_DOCUMENTS_HEADING = `Supplemental documents`
export const SEND_SUPPLEMENTAL_LABEL = `Send supplemental documents`
export const SEND_SUPPLEMENTAL_BUSY_LABEL = `Sending supplemental documents`
export const RETRY_SUPPLEMENTAL_LABEL = `Try again`
export const RETRY_SUPPLEMENTAL_BUSY_LABEL = `Trying again`
export const RESEND_SUPPLEMENTAL_LABEL = `Resend`
export const RESEND_SUPPLEMENTAL_BUSY_LABEL = `Resending`

/**
 * What the supplemental package is doing, in the seller's words. The reason a
 * failure gives comes from the server and is shown beside this, so nothing here
 * tries to explain a specific failure.
 */
export const SUPPLEMENTAL_STATUS_COPY: ReadonlyMap<
  string,
  { label: string; detail: string; tone: ConcertOutcomeTone }
> = new Map(
  Object.entries({
    claimed: {
      label: `Sending`,
      detail: `Vision is sending the supplemental documents. This screen refreshes when it finishes.`,
      tone: `gold`,
    },
    sent: {
      label: `Sent for signature`,
      detail: `Vision emailed the supplemental documents to the homeowner, separately from Concert's loan documents.`,
      tone: `success`,
    },
    not_applicable: {
      label: `None apply`,
      detail: `No additional documents apply to this proposal, so nothing was sent. Nothing needs fixing.`,
      tone: `muted`,
    },
    failed: {
      label: `Send failed`,
      detail: `Vision could not send the supplemental documents.`,
      tone: `danger`,
    },
    blocked: {
      label: `Blocked`,
      detail: `The supplemental documents could not be prepared.`,
      tone: `danger`,
    },
    send_unknown: {
      label: `Outcome unknown`,
      detail: `Vision did not get an answer while sending, so whether the homeowner received the documents is not yet known.`,
      tone: `gold`,
    },
    superseded: {
      label: `Replaced`,
      detail: `A newer contract package replaced this one. It is kept for history.`,
      tone: `muted`,
    },
    // A Map, not an object literal: the state is a string off the wire, and an
    // object would answer `constructor` or `toString` with an inherited value the
    // seller would then read as a status.
  }),
)

/** Concert has confirmed a package, but nothing has been sent for it yet. */
export const SUPPLEMENTAL_NOT_STARTED_DETAIL = `Vision has not sent supplemental documents for this package yet.`

/** There is no confirmed Concert package to add documents to. */
export const SUPPLEMENTAL_NO_PACKAGE_DETAIL = `Supplemental documents are sent once Concert confirms the loan documents went to the homeowner.`

export const SUPPLEMENTAL_SIGNED_DETAIL = `The homeowner signed the supplemental documents.`

/** DocuSign closed the envelope without a signature. */
export const SUPPLEMENTAL_CLOSED_DETAIL: Record<string, string> = {
  declined: `The homeowner declined the supplemental documents. Contact support before sending them again.`,
  voided: `The supplemental envelope was voided. Contact support before sending these documents again.`,
  expired: `The supplemental envelope expired before it was signed. Contact support before sending these documents again.`,
}
